import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Trash2, Mail, Phone, MapPin } from "lucide-react";

export default function DispoBuyersList() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dispo/buyers-list");
      setBuyers(data.buyers || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load buyers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeBuyer = async (id) => {
    if (!confirm("Remove this buyer from your list? They will stop receiving alerts.")) return;
    try {
      await api.delete(`/dispo/buyers-list/${id}`);
      setBuyers((b) => b.filter((x) => x.id !== id));
      toast.success("Buyer removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="dispo-buyers-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bell size={20} className="text-[var(--gold,#F59E0B)]" />
            <h1 className="font-display font-extrabold text-3xl tracking-tight">Buyer List</h1>
            <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">Dispo Studio · Week 2</span>
          </div>
          <p className="text-sm text-[var(--ink-2)] max-w-2xl">
            Cash buyers who opted in from your Deal Rooms. When you publish a new deal that matches their criteria, they get an email with the numbers and a link to make an offer.
          </p>
        </div>
        <Link to="/app/dispo" className="btn-ghost text-sm" data-testid="dispo-buyers-back">← Back to Deal Rooms</Link>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[var(--ink-3)]" data-testid="dispo-buyers-loading">Loading…</div>
      ) : buyers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--line)] p-10 text-center bg-white" data-testid="dispo-buyers-empty">
          <Bell size={40} className="mx-auto text-[var(--ink-3)] mb-3" />
          <h3 className="font-display font-bold text-xl mb-1">No buyers yet</h3>
          <p className="text-sm text-[var(--ink-2)] max-w-md mx-auto">
            Share a Deal Room link — every public page includes a &ldquo;Get alerted when a deal like this drops&rdquo; signup CTA. Buyers land here when they opt in.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--line)] bg-white overflow-hidden" data-testid="dispo-buyers-list">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-[var(--ink-3)] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Buyer</th>
                <th className="text-left px-4 py-3 font-semibold">Criteria</th>
                <th className="text-left px-4 py-3 font-semibold">Signed up</th>
                <th className="text-right px-4 py-3 font-semibold">Matches</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {buyers.map((b) => (
                <tr key={b.id} className="border-t border-[var(--line)] align-top" data-testid={`dispo-buyer-${b.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{b.name || "—"}</div>
                    <div className="text-xs text-[var(--ink-2)] flex items-center gap-1 mt-0.5">
                      <Mail size={11} />{b.email}
                    </div>
                    {b.phone && (
                      <div className="text-xs text-[var(--ink-2)] flex items-center gap-1">
                        <Phone size={11} />{b.phone}
                      </div>
                    )}
                    {b.unsubscribed && (
                      <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded mt-1" style={{ background: "#FEE2E2", color: "#991B1B" }}>Unsubscribed</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--ink-2)]">
                    <CriteriaSummary crit={b.criteria || {}} />
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap text-[var(--ink-2)]">
                    {formatWhen(b.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{b.match_count ?? 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      className="text-[var(--ink-3)] hover:text-red-600"
                      onClick={() => removeBuyer(b.id)}
                      title="Remove buyer"
                      data-testid={`dispo-buyer-remove-${b.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CriteriaSummary({ crit }) {
  const parts = [];
  if (crit.states?.length) parts.push(<span key="s" className="inline-flex items-center gap-1"><MapPin size={10} />{crit.states.join(", ")}</span>);
  if (crit.cities?.length) parts.push(<span key="c">{crit.cities.join(", ")}</span>);
  if (crit.price_min != null || crit.price_max != null) {
    parts.push(<span key="p">{fmtMoney(crit.price_min)}–{fmtMoney(crit.price_max)}</span>);
  }
  if (crit.min_beds) parts.push(<span key="b">{crit.min_beds}+ bd</span>);
  if (crit.strategies?.length) parts.push(<span key="st">{crit.strategies.join(", ")}</span>);
  if (!parts.length) return <span className="text-[var(--ink-3)]">Any deal</span>;
  return <div className="flex flex-wrap gap-x-3 gap-y-0.5">{parts}</div>;
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return iso;
  }
}
